import { DocumentSubjectType, DocumentTypeAttributes, DocumentTypeOrientation, DocumentTypeSize } from '@/types'
import { CreateDocumentTypeMutation } from './__generated__/create_mutation'
import { Mutation as CreateMutation } from './create_mutation'
import { useForm } from '@/helpers/useForm'
import { Selector as SubjectSelector } from './subject/selector'
import { Selector as SizeSelector } from './size/selector'
import { Selector as OrientationSelector } from './orientation/selector'
import { useNavigate } from 'react-router'
import { useEffect } from 'react'

interface Props {
  onSuccess: () => void
}

export const Form = ({ onSuccess }: Props) => {
  const navigate = useNavigate()

  const {
    save,
    saveSuccess,
    formBuilder,
    mutationLoading,
    mutationData,
    attributes,
    setAttributes,
    fieldErrors,
  } = useForm<CreateDocumentTypeMutation, DocumentTypeAttributes, never, never>({
    defaultValues: {
      name: '',
      subject: DocumentSubjectType.AssetType,
      incremental: true,
      incrementStart: 1,
      dateDue: true,
      dateIssued: true,
      size: DocumentTypeSize.A4,
      orientation: DocumentTypeOrientation.Portrait,
    },
    mutation: CreateMutation,
    resultKey: 'createDocumentType'
  })

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  useEffect(() => {
    if (!mutationData?.createDocumentType?.documentType?.id) return

    navigate(`/settings/document-types/${mutationData?.createDocumentType?.documentType?.id}`)
  }, [mutationData, navigate])

  return (
    <>
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { formBuilder.buildField({ name: 'Name', field: 'name', type: 'text' }) }
        { formBuilder.buildCustomField({
          name: 'Subject',
          field: 'subject',
          input: <SubjectSelector
            invalid={!!fieldErrors?.subject}
            value={attributes.subject}
            onChange={value => setAttributes({
              ...attributes,
              subject: value?.id as DocumentSubjectType || DocumentSubjectType.AssetType
            }) }
          />
        }) }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildCustomField({
            name: 'Size',
            field: 'size',
            input: <SizeSelector
              invalid={!!fieldErrors?.size}
              value={attributes.size}
              onChange={value => setAttributes({
                ...attributes,
                size: value?.id as DocumentTypeSize || DocumentTypeSize.A4
              }) }
            />
          }) }
          { formBuilder.buildCustomField({
            name: 'Orientation',
            field: 'orientation',
            input: <OrientationSelector
              invalid={!!fieldErrors?.orientation}
              value={attributes.orientation}
              onChange={value => setAttributes({
                ...attributes,
                orientation: value?.id as DocumentTypeOrientation || DocumentTypeOrientation.Portrait
              }) }
            />
          }) }
        </div>
        <div className="grid grid-cols-2 gap-6 items-center">
          { formBuilder.buildField({ name: 'Incremental', field: 'incremental', type: 'switch' }) }
          { attributes.incremental &&
            formBuilder.buildField({ name: 'Start number', field: 'incrementStart', type: 'positive-integer' }) }
        </div>
        <div className="grid grid-cols-2 gap-6 items-center">
          { formBuilder.buildField({ name: 'Date due', field: 'dateDue', type: 'switch' }) }
          { formBuilder.buildField({ name: 'Date issued', field: 'dateIssued', type: 'switch' }) }
        </div>
        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </>
  )
}
